import type { Logged, Turn } from "../activity.ts";

export type InspectorFilter = "all" | "messages" | "tools" | "delegations" | "system";
export type InspectorEvent = {
  key: string;
  at: string;
  category: Exclude<InspectorFilter, "all">;
  kind: string;
  ordinal?: number;
  payload: Record<string, unknown>;
};

function safePayload(step: Logged): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const allowed = step.kind.startsWith("run.tool.")
    ? ["call", "tool", "object", "ok"]
    : step.kind === "run.retry"
      ? ["attempt"]
      : step.kind === "run.usage"
        ? ["tokens", "amount"]
        : step.kind === "run.telemetry.dropped"
          ? ["count"]
          : step.kind === "run.stopped"
            ? ["how"]
            : [];
  for (const key of allowed) if (step.payload[key] !== undefined) payload[key] = step.payload[key];
  return payload;
}

function time(at: string): number {
  const parsed = Date.parse(at);
  return Number.isFinite(parsed) ? parsed : 0;
}

function stepOrder(a: Logged, b: Logged): number {
  const byTime = time(a.at) - time(b.at);
  if (byTime !== 0) return byTime;
  const byKind = a.kind.localeCompare(b.kind);
  if (byKind !== 0) return byKind;
  return JSON.stringify(safePayload(a)).localeCompare(JSON.stringify(safePayload(b)));
}

type ToolFold = {
  run: string;
  call: number;
  startedAt?: string;
  endedAt?: string;
  startedTool?: unknown;
  startedObject?: unknown;
  finishedTool?: unknown;
  finishedObject?: unknown;
  ok?: unknown;
};

function toolEvent(tool: ToolFold): InspectorEvent {
  const payload: Record<string, unknown> = { call: tool.call };
  const name = tool.startedTool ?? tool.finishedTool;
  const object = tool.startedObject ?? tool.finishedObject;
  if (name !== undefined) payload.tool = name;
  if (object !== undefined) payload.object = object;
  if (tool.ok !== undefined) payload.ok = tool.ok;
  if (tool.startedAt !== undefined) payload.startedAt = tool.startedAt;
  if (tool.endedAt !== undefined) payload.endedAt = tool.endedAt;
  if (tool.startedAt !== undefined && tool.endedAt !== undefined) {
    payload.durationMs = Math.max(0, time(tool.endedAt) - time(tool.startedAt));
  }
  return {
    key: `tool:${tool.run}:${tool.call}`,
    at: tool.startedAt ?? tool.endedAt ?? "",
    category: "tools",
    kind: tool.endedAt === undefined ? "run.tool.started" : "run.tool.finished",
    ordinal: tool.call,
    payload,
  };
}

export function inspectorEvents(turn: Turn): InspectorEvent[] {
  const events: InspectorEvent[] = [];
  const tools = new Map<string, ToolFold>();
  for (const step of [...(turn.steps ?? [])].sort(stepOrder)) {
    const call = typeof step.payload.call === "number" ? step.payload.call : undefined;
    if ((step.kind === "run.tool.started" || step.kind === "run.tool.finished") && call !== undefined) {
      const key = `${step.run}:${call}`;
      const tool = tools.get(key) ?? { run: step.run, call };
      if (step.kind === "run.tool.started") {
        tool.startedAt ??= step.at;
        tool.startedTool ??= step.payload.tool;
        tool.startedObject ??= step.payload.object;
      } else if (step.kind === "run.tool.finished") {
        tool.endedAt = step.at;
        if (step.payload.tool !== undefined) tool.finishedTool = step.payload.tool;
        if (step.payload.object !== undefined) tool.finishedObject = step.payload.object;
        if (step.payload.ok !== undefined) tool.ok = step.payload.ok;
      }
      tools.set(key, tool);
      continue;
    }
    const category = step.kind.startsWith("run.tool.")
      ? "tools"
      : step.kind === "run.message"
        ? "messages"
        : "system";
    events.push({
      key: `${step.kind}:${step.at}:${String(step.payload.call ?? "")}`,
      at: step.at,
      category,
      kind: step.kind,
      ordinal: call,
      payload: safePayload(step),
    });
  }
  events.push(...[...tools.values()].map(toolEvent));
  for (const child of turn.delegations ?? []) {
    events.push({
      key: `delegation:${child.run}`,
      at: child.startedAt ?? child.lastActivityAt ?? turn.startedAt ?? "",
      category: "delegations",
      kind: "run.delegation",
      payload: {
        state: child.state,
        toolCount: child.toolCount,
        lastActivityAt: child.lastActivityAt,
      },
    });
  }
  return events.sort((a, b) => time(a.at) - time(b.at) || a.key.localeCompare(b.key));
}

export function filteredEvents(events: readonly InspectorEvent[], filter: InspectorFilter): InspectorEvent[] {
  return filter === "all" ? [...events] : events.filter((event) => event.category === filter);
}

export function heartbeatAge(heartbeatAt: string | null | undefined, now: number): number | null {
  if (!heartbeatAt) return null;
  const age = now - Date.parse(heartbeatAt);
  return Number.isFinite(age) ? Math.max(0, age) : null;
}

export function turnWithSteps(turn: Turn, steps: readonly Logged[]): Turn {
  return { ...turn, steps: steps.filter((step) => step.run === turn.run) };
}
